import React, { useCallback, useEffect, useState } from "react";
import { Button } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import "./GuidedTour.css";

const CARD_WIDTH = 320;
const GAP = 12;
const PAD = 8;

// All tours share one localStorage array instead of one key per
// screen/hint, so the "seen" state doesn't sprawl across dozens of entries.
const FLAGS_KEY = "guided_tour_flags";
const LEGACY_PREFIXES = ["tour_seen_", "tour_hint_dismissed_"];

const readFlags = () => {
  try {
    const raw = localStorage.getItem(FLAGS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (e) {
    return [];
  }
};

const writeFlags = (flags) => {
  try {
    localStorage.setItem(FLAGS_KEY, JSON.stringify(flags));
  } catch (e) {
    // ignore storage failures (private mode etc.)
  }
};

// One-time sweep of the old per-tour keys into the consolidated array, so
// guides a user already dismissed don't pop back up after this change.
const migrateLegacyFlags = () => {
  let flags = readFlags();
  try {
    const legacyKeys = Object.keys(localStorage).filter((k) =>
      LEGACY_PREFIXES.some((p) => k.startsWith(p))
    );
    legacyKeys.forEach((k) => {
      if (!flags.includes(k)) flags = [...flags, k];
      localStorage.removeItem(k);
    });
    if (legacyKeys.length) writeFlags(flags);
  } catch (e) {
    // ignore storage failures (private mode etc.)
  }
};
migrateLegacyFlags();

export const hasTourFlag = (key) => readFlags().includes(key);

export const setTourFlag = (key) => {
  const flags = readFlags();
  if (!flags.includes(key)) writeFlags([...flags, key]);
};

/**
 * Generic dependency-free spotlight walkthrough. Steps target real DOM nodes
 * via CSS selector, so no extra library / npm install is needed for this.
 *
 * The host is expected to only render this component while the tour should
 * be visible (e.g. `{isTourOpen && <GuidedTour ... />}`) rather than always
 * rendering it with an `open` flag - that keeps every run a fresh mount, so
 * a skipped/closed tour always restarts from step 1 next time instead of
 * resuming wherever it was left off.
 */
const GuidedTour = ({ steps, onClose, storageKey, onStepChange }) => {
  const [stepIndex, setStepIndex] = useState(0);
  const [rect, setRect] = useState(null);

  useEffect(() => {
    onStepChange?.(steps[stepIndex] || null);
  }, [stepIndex, steps, onStepChange]);

  const measureCurrentTarget = useCallback(() => {
    const step = steps[stepIndex];
    if (!step) return false;
    const el = document.querySelector(step.target);
    if (!el) return false;
    el.scrollIntoView({ block: "center", behavior: "smooth" });
    requestAnimationFrame(() => {
      const r = el.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    });
    return true;
  }, [steps, stepIndex]);

  // A step's target can be something the host screen only mounts in response
  // to onStepChange above (e.g. a preview popup), and that popup can still be
  // mid-way through its own open transition (antd's zoom-in) when it first
  // appears - so keep remeasuring for a short window instead of stopping at
  // the first successful read, or the spotlight freezes at its pre-animation position.
  useEffect(() => {
    setRect(null);
    const delays = [0, 100, 250, 400, 600, 850];
    const timers = delays.map(d => setTimeout(measureCurrentTarget, d));
    return () => timers.forEach(clearTimeout);
  }, [stepIndex, measureCurrentTarget]);

  useEffect(() => {
    window.addEventListener("resize", measureCurrentTarget);
    window.addEventListener("scroll", measureCurrentTarget, true);
    return () => {
      window.removeEventListener("resize", measureCurrentTarget);
      window.removeEventListener("scroll", measureCurrentTarget, true);
    };
  }, [measureCurrentTarget]);

  useEffect(
    () => () => {
      // Unmounting (host closed the tour) counts as "leaving" whatever step
      // we were on, so a preview popup tied to that step gets torn down too.
      onStepChange?.(null);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  if (!steps.length) return null;

  const step = steps[stepIndex];
  const isLast = stepIndex === steps.length - 1;

  const finish = () => {
    if (storageKey) setTourFlag(storageKey);
    onClose();
  };

  // A small icon reads better as a circular highlight than a boxy rectangle.
  const spotlightPad = step.shape === "circle" ? Math.max(rect?.width || 0, rect?.height || 0) * 0.4 + PAD : PAD;
  const spotlightStyle = rect
    ? {
        top: rect.top - spotlightPad,
        left: rect.left - spotlightPad,
        width: rect.width + spotlightPad * 2,
        height: rect.height + spotlightPad * 2,
        borderRadius: step.shape === "circle" ? "50%" : undefined,
      }
    : null;

  const viewportH = window.innerHeight;
  const viewportW = window.innerWidth;
  // Narrower phones can't fit a fixed 320px card without overflowing the
  // viewport - shrink to fit instead of letting it run off-screen.
  const cardWidth = Math.min(CARD_WIDTH, viewportW - 32);
  // A target that covers most of the viewport (a whole modal, say) can't be
  // hugged above/below without the card overlapping it - dock to a corner instead.
  const targetIsLarge = rect && (rect.height > viewportH * 0.55 || rect.width > viewportW * 0.7);
  const placeBelow = rect ? rect.top + rect.height + 220 < viewportH : true;
  const cardPositionStyle = targetIsLarge
    ? { bottom: 20, right: 20 }
    : {
        top: rect
          ? placeBelow
            ? rect.top + rect.height + PAD + GAP
            : Math.max(rect.top - PAD - GAP - 180, 16)
          : viewportH / 2 - 110,
        left: rect
          ? Math.min(Math.max(rect.left, 16), viewportW - cardWidth - 16)
          : Math.max((viewportW - cardWidth) / 2, 16),
      };

  return (
    <div className="guided-tour-root">
      <div className="guided-tour-backdrop" onClick={finish} />
      {spotlightStyle && <div className="guided-tour-spotlight" style={spotlightStyle} />}
      <div className="guided-tour-card" style={{ ...cardPositionStyle, width: cardWidth }}>
        <button type="button" className="guided-tour-close" onClick={finish} aria-label="Close guide">
          <CloseOutlined />
        </button>
        {steps.length > 1 && (
          <div className="guided-tour-step-count">
            Step {stepIndex + 1} of {steps.length}
          </div>
        )}
        <h4 className="guided-tour-title">{step.title}</h4>
        <p className="guided-tour-content">{step.content}</p>
        <div className="guided-tour-footer">
          <div className="guided-tour-dots">
            {steps.length > 1 &&
              steps.map((_, i) => (
                <span key={i} className={`guided-tour-dot${i === stepIndex ? " active" : ""}`} />
              ))}
          </div>
          <div className="guided-tour-actions">
            {stepIndex > 0 && (
              <Button size="small" onClick={() => setStepIndex(i => i - 1)}>
                Back
              </Button>
            )}
            {steps.length > 1 && (
              <Button size="small" type="text" onClick={finish}>
                Skip
              </Button>
            )}
            {!isLast ? (
              <Button size="small" type="primary" onClick={() => setStepIndex(i => i + 1)}>
                Next
              </Button>
            ) : (
              <Button size="small" type="primary" onClick={finish}>
                Got it
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GuidedTour;
